export const ORG_TYPES = [
  'Listed corporation', 'Private', 'Consultancy', 'NGO', 'Government', 'Start-up', 'Financial services', 'Academic', 'Other'
];

export const CAREER_STAGES = [
  'Early career', 'Mid-career', 'Senior', 'Director VP', 'C-suite Partner'
];

export const EDUCATION_LEVELS = [
  "Bachelor's", "Master's", 'MBA', 'PhD/Doctorate', "Associate's/Diploma", 'Currently enrolled', 'No degree'
];

export const FIELDS_OF_STUDY = [
  'Environmental Science/Studies', 'Engineering', 'Business/Management', 'Economics/Finance',
  'Policy/Political Science', 'Natural Sciences', 'Social Sciences', 'Law', 'Data Science/Analytics',
  'Communications/Journalism', 'Architecture/Urban Planning', 'Agriculture/Food Science', 'Public Health', 'Other'
];

export const SKILLS_REPORTING = [
  'GHG Protocol/Scope 3', 'TCFD', 'CSRD/ESRS', 'ISSB/IFRS S1-S2', 'GRI Standards',
  'CDP', 'TNFD', 'SBTs', 'EU Taxonomy', 'SFDR'
];

export const SKILLS_ANALYSIS = [
  'Materiality assessment', 'LCA', 'Climate scenario analysis', 'ESG data analysis',
  'Supply chain mapping', 'Impact measurement', 'Net-zero strategy', 'Carbon markets'
];

export const SKILLS_LEADERSHIP = [
  'Board-level comms', 'Stakeholder engagement', 'Cross-functional influence', 'Systems thinking',
  'Change management', 'Policy advocacy', 'Team leadership'
];

export const TRANSFERABLE_SKILLS = [
  'Data analysis & visualization', 'Project management', 'Financial modeling & budgeting',
  'Research methodology', 'Technical writing & reporting', 'Policy analysis', 'Stakeholder management',
  'Process optimization', 'Risk assessment', 'Regulatory compliance', 'Strategic planning',
  'Grant writing', 'Lab/field research', 'Software development', 'Marketing & communications',
  'Operations management', 'Sales & business development', 'Legal/contract review',
  'Teaching & training', 'People management'
];

export const ACADEMIC_SKILLS = [
  'Quantitative research methods', 'Peer-reviewed publishing', 'Grant writing & funding',
  'Statistical analysis (R Python SPSS)', 'Literature review & synthesis', 'Teaching & curriculum design',
  'Field research & data collection', 'Interdisciplinary collaboration', 'Conference presentation',
  'Thesis/dissertation supervision', 'Lab management', 'Policy brief writing'
];

export const ACADEMIC_TRIGGERS = ["Master's", 'PhD/Doctorate', 'Currently enrolled'];

export const PIVOT_TYPES_ENTERING = [
  {
    id: 'enter-from-industry',
    title: 'Enter from Industry',
    desc: "I work in another sector and want to transition into sustainability",
  },
  {
    id: 'enter-from-academia',
    title: 'Enter from Academia',
    desc: "I'm a researcher / professor / postdoc moving to industry",
  },
  {
    id: 'recent-graduate',
    title: 'Recent Graduate',
    desc: "Student or recent grad looking for first sustainability role",
  },
];

export const PIVOT_TYPES_INSUST = [
  { id: 'level-up',       title: 'Level Up',         desc: 'Move to the next seniority level in your current track' },
  // Merged card per Part 4 spec — replaces the old `lateral-sust` + `sust-to-adjacent` pair.
  // `shift-sideways` covers a change of focus, field, or setting at the same level.
  { id: 'shift-sideways', title: 'Shift Sideways',   desc: 'Change your focus, field, or setting without changing level — reporting to strategy, corporate to consulting, or energy to climate finance' },
  { id: 'return',         title: 'Between Roles',    desc: 'Coming back in after a break, layoff, or reset. A re-entry plan pitched at your level.' },
  { id: 'portfolio',      title: 'Portfolio Career', desc: 'Consulting + board seats + advisory' },
];

export const HOURS_PER_WEEK = ['1-2 hrs', '3-5 hrs', '5-10 hrs', '10+ hrs'];
export const URGENCY = ['Exploring (no rush)', 'Within 6 months', 'Within 3 months', 'ASAP'];
export const SITUATIONS = ['Employed full-time', 'Part-time', 'Between jobs', 'Freelance', 'Student'];

/* ---------- Domain detection (auto-detected from CV in Step 0, editable in Step 2) ---------- */

// Full option list matches backend prompt & schema.
// =============================================================================
// DOMAIN (subject matter) — 3 canonical options only.
// Corporate sustainability, ESG, climate, circular economy, supply-chain
// sustainability all live INSIDE `sustainability_esg_csr`. `energy_cleantech`
// is only for genuinely technical energy work. `ehs` is only for EHS.
// =============================================================================
export const DOMAINS = [
  { id: 'sustainability_esg_csr', label: 'Sustainability / ESG / CSR',    group: 'sustainability',
    description: 'Corporate sustainability, ESG, climate, circular economy, supply chain, nature' },
  { id: 'energy_cleantech',       label: 'Energy & Cleantech',            group: 'sustainability',
    description: 'Renewables, grid, storage, PPAs, power markets, clean tech' },
  { id: 'ehs',                    label: 'Environmental Health & Safety', group: 'sustainability',
    description: 'EHS, industrial hygiene, remediation, permitting, safety systems' },
];

// =============================================================================
// FUNCTION (the work you do) — 6 canonical options.
// "Advisory & Consulting" and "Portfolio" are engine OUTPUTS (destinations the
// engine recommends), not inputs — deliberately excluded from this list.
// =============================================================================
export const FUNCTIONS = [
  { id: 'strategy_targets',     label: 'Strategy & Target-Setting',
    description: 'Owning sustainability strategy, targets, roadmaps, board-level direction' },
  { id: 'reporting_disclosure', label: 'Reporting & Disclosure',
    description: 'ESG reporting, frameworks, data, assurance' },
  { id: 'program_delivery',     label: 'Program & Project Delivery',
    description: 'Implementing initiatives, decarbonization programs, operations' },
  { id: 'finance_investment',   label: 'Finance & Investment',
    description: 'ESG investing, green finance, impact capital, sustainable lending' },
  { id: 'policy_regulation',    label: 'Policy & Regulation',
    description: 'Policy, government relations, regulatory affairs, public sector' },
  { id: 'comms_engagement',     label: 'Communications & Engagement',
    description: 'Communications, stakeholder engagement, external affairs, storytelling' },
];

// Domain-specific framework chips shown in Step 2 (superset per domain).
// Chip PRE-SELECTION is driven by `detected_frameworks` from the CV extractor.
export const DOMAIN_FRAMEWORKS = {
  sustainability_esg_csr: [
    // Corporate sustainability + ESG + climate + circular + supply chain, all
    // merged since these are one domain now.
    'GHG Protocol', 'Scope 3', 'TCFD', 'CSRD/ESRS', 'ISSB/IFRS S1-S2', 'GRI Standards',
    'CDP', 'TNFD', 'SBTi', 'EU Taxonomy', 'SFDR', 'Double materiality', 'PCF / LCA',
    'Impact measurement (IRIS+)', 'Climate risk assessment', 'Nature-based solutions',
    'Biodiversity metrics', 'Circular Economy Strategies', 'EPR / PPWR',
    'EUDR', 'Supply Chain Due Diligence', 'EcoVadis', 'Just Transition frameworks',
  ],
  energy_cleantech: [
    'PPA structuring', 'Grid modernization', 'FERC/NERC compliance', 'Energy modeling',
    'Battery storage systems', 'LCOE analysis', 'Renewable project development',
    'Demand response', 'Energy auditing', 'ISO 50001', 'Distributed energy resources',
    'Power market analysis',
  ],
  ehs: [
    'OSHA compliance', 'ISO 14001', 'Hazardous waste management', 'Air quality permitting',
    'Water discharge (NPDES)', 'Remediation oversight', 'Safety management systems',
    'Environmental auditing', 'Incident investigation', 'CERCLA/RCRA',
  ],
};

// All domains are treated as "already inside" sustainability now (there are no
// "outside" domains in the input dropdown — cross-over pivot is signaled via
// pivot_type: enter-from-industry / enter-from-academia / recent-graduate).
export const SUSTAINABILITY_DOMAINS = new Set([
  'sustainability_esg_csr', 'energy_cleantech', 'ehs',
]);


export const DEMO_PROFILE_TEXT = `Sarah Chen
Senior ESG Analyst at GreenFin Capital — London, UK

Summary
ESG analyst with 6 years building corporate climate disclosures and portfolio-level Scope 3 models. Currently leading CSRD readiness for 12 financial-sector clients and co-authored the methodology for a £600m climate transition fund.

Experience
- Senior ESG Analyst, GreenFin Capital (2022 - Present): Portfolio Scope 3 model covering 84 holdings; CSRD readiness for 12 clients; co-authored £600m transition fund methodology; advised investment committee on biodiversity exposure.
- ESG Consultant, Carbon Trust (2019 - 2022): Delivered TCFD reports for 17 FTSE-100 companies; built sector-specific decarbonisation roadmaps for cement and aviation; trained 40+ corporate sustainability teams.
- Energy Analyst, EDF Energy (2018 - 2019): Built grid emissions modelling tool used by the trading desk; supported power market forecasting.

Education
- MSc Environmental Economics, London School of Economics (2018)
- BSc Economics, University of Manchester (2016)

Skills
GHG Protocol/Scope 3, TCFD, CSRD/ESRS, ISSB, materiality assessment, climate scenario analysis, ESG data analytics, supply chain emissions, SBTs, Python, R, Power BI, stakeholder engagement, board-level comms.

Achievements
- Speaker at COP27 side event on financed emissions.
- Published in Journal of Sustainable Finance & Investment.
`;
